/** Real client for `/api/v1/pos/*` (api/app/pos_api.py). Same origin + session cookie as the chat API. */
import { ApiError } from '../client'
import { reportUnauthorized } from '../http'
import type {
  PosApi,
  PosCartBody,
  PosCatalog,
  PosChatOrderState,
  PosContext,
  PosCouponCheck,
  PosCouponCheckBody,
  PosCustomer,
  PosCustomerCreate,
  PosCustomerCreated,
  PosHistory,
  PosHoldEditBody,
  PosOrderBody,
  PosOrderResult,
  PosOpenOrder,
  PosOrderStatus,
  PosPayBody,
  PosPinResult,
  PosPinSet,
  PosQr,
  PosQuote,
  PosSharedPlace,
  PosRenewBody,
} from './types'

/** An error from the POS proxy: calm Thai `message` + machine `reason` (+ POS detail / extra fields). */
export class PosApiError extends ApiError {
  constructor(
    status: number,
    message: string,
    readonly reason: string,
    readonly detail: unknown = null,
    readonly extra: Record<string, unknown> = {},
  ) {
    super(status, message)
    this.name = 'PosApiError'
  }

  /** No answer from the POS: pressing save again with the same click is safe (the POS never writes twice). */
  get retrySame(): boolean {
    return this.extra.retry_same === true || this.status === 0 || this.status >= 500
  }

  /** The save of this chat still waiting for the POS (posUnconfirmed / orderUnresolved), when the server sent it. */
  get openOrder(): PosOpenOrder | null {
    const o = this.extra.open_order
    return o && typeof o === 'object' ? (o as PosOpenOrder) : null
  }
}

type Query = Record<string, string | number | null | undefined>

export class HttpPosApi implements PosApi {
  constructor(
    private readonly base: string,
    private readonly timeoutMs = 15000,
    /** POST orders waits for the server's own retries (≤ 45 s). */
    private readonly orderTimeoutMs = 60000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private url(path: string, q?: Query): string {
    const u = new URL(`${this.base.replace(/\/+$/, '')}/api/v1/pos${path}`, window.location.origin)
    for (const [k, v] of Object.entries(q ?? {})) {
      if (v !== null && v !== undefined && v !== '') u.searchParams.set(k, String(v))
    }
    return u.toString()
  }

  private async req<T>(
    method: string,
    path: string,
    opts: { query?: Query; body?: unknown; timeoutMs?: number } = {},
  ): Promise<T> {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? this.timeoutMs)
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
    try {
      const res = await this.fetchImpl(this.url(path, opts.query), {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      if (res.status === 401) reportUnauthorized()
      if (!res.ok) {
        let message = res.statusText || `HTTP ${res.status}`
        let reason = `http${res.status}`
        let detail: unknown = null
        const extra: Record<string, unknown> = {}
        try {
          const j = (await res.json()) as Record<string, unknown>
          if (typeof j.detail === 'string') message = j.detail
          else if (Array.isArray(j.detail)) message = 'ข้อมูลในฟอร์มไม่ครบหรือไม่ถูกต้อง'
          if (typeof j.reason === 'string') reason = j.reason
          detail = j.pos_detail ?? null
          for (const [k, v] of Object.entries(j)) if (!['detail', 'reason', 'pos_detail'].includes(k)) extra[k] = v
        } catch {
          /* body not JSON */
        }
        throw new PosApiError(res.status, message, reason, detail, extra)
      }
      return (await res.json()) as T
    } catch (e) {
      if (e instanceof ApiError) throw e
      if (e instanceof DOMException && e.name === 'AbortError') {
        throw new PosApiError(0, 'ระบบไม่ตอบในเวลาที่กำหนด ลองใหม่อีกครั้ง', 'timeout')
      }
      throw new PosApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง', 'network')
    } finally {
      clearTimeout(timer)
    }
  }

  catalog(): Promise<PosCatalog> {
    return this.req('GET', '/catalog')
  }

  context(conversationId: string): Promise<PosContext> {
    return this.req('GET', `/conversations/${encodeURIComponent(conversationId)}`)
  }

  async searchCustomers(q: string, opts: { exactMobile?: boolean } = {}): Promise<PosCustomer[]> {
    const query: Record<string, string> = { q }
    if (opts.exactMobile) query.exact = '1'
    const r = await this.req<{ customers: PosCustomer[] }>('GET', '/customers', { query })
    return r.customers
  }

  createCustomer(body: PosCustomerCreate): Promise<PosCustomerCreated> {
    return this.req('POST', '/customers', { body, timeoutMs: 30000 })
  }

  /** The POS asks Google for the road distance (≤ 8 s) and the server retries once: wait like createCustomer. */
  setCustomerPin(customerId: string, body: PosPinSet): Promise<PosPinResult> {
    return this.req('POST', `/customers/${encodeURIComponent(customerId)}/pin`, { body, timeoutMs: 45000 })
  }

  async sharedPin(conversationId: string): Promise<PosSharedPlace | null> {
    const r = await this.req<{ place: PosSharedPlace | null }>('GET', `/conversations/${encodeURIComponent(conversationId)}/shared-pin`)
    return r.place
  }

  async linkCustomer(conversationId: string, customerId: string): Promise<PosCustomer> {
    const r = await this.req<{ customer: PosCustomer }>(
      'PUT',
      `/conversations/${encodeURIComponent(conversationId)}/customer`,
      { body: { customer_id: Number(customerId) } },
    )
    return r.customer
  }

  async unlinkCustomer(conversationId: string): Promise<void> {
    await this.req('DELETE', `/conversations/${encodeURIComponent(conversationId)}/customer`)
  }

  customerOrders(customerId: string, limit = 20): Promise<PosHistory> {
    return this.req('GET', `/customers/${encodeURIComponent(customerId)}/orders`, { query: { limit } })
  }

  orderStatus(key: string): Promise<PosOrderStatus> {
    return this.req('GET', `/orders/${encodeURIComponent(key)}`)
  }

  quote(body: PosCartBody): Promise<PosQuote> {
    return this.req('POST', '/quote', { body })
  }

  checkCoupon(body: PosCouponCheckBody): Promise<PosCouponCheck> {
    return this.req('POST', '/coupon/check', { body })
  }

  createOrder(body: PosOrderBody): Promise<PosOrderResult> {
    return this.req('POST', '/orders', { body, timeoutMs: this.orderTimeoutMs })
  }

  chatOrder(id: string): Promise<PosChatOrderState> {
    return this.req('GET', `/chat-orders/${encodeURIComponent(id)}`)
  }

  checkChatOrder(id: string): Promise<PosChatOrderState> {
    return this.req('POST', `/chat-orders/${encodeURIComponent(id)}/check`, { timeoutMs: this.orderTimeoutMs })
  }

  abandonChatOrder(id: string): Promise<PosChatOrderState> {
    return this.req('POST', `/chat-orders/${encodeURIComponent(id)}/abandon`)
  }

  promptpayQr(id: string): Promise<PosQr> {
    return this.req('POST', `/chat-orders/${encodeURIComponent(id)}/promptpay`, { timeoutMs: 30000 })
  }

  payChatOrder(id: string, body: PosPayBody): Promise<PosChatOrderState & { replayed?: boolean }> {
    // the server retries the POS for ≤ 45 s (like POST orders)
    return this.req('POST', `/chat-orders/${encodeURIComponent(id)}/pay`, { body, timeoutMs: this.orderTimeoutMs })
  }

  editChatOrder(id: string, body: PosHoldEditBody): Promise<PosChatOrderState & { replayed?: boolean }> {
    return this.req('PUT', `/chat-orders/${encodeURIComponent(id)}`, { body, timeoutMs: this.orderTimeoutMs })
  }

  cancelChatOrder(id: string): Promise<PosChatOrderState> {
    return this.req('POST', `/chat-orders/${encodeURIComponent(id)}/cancel`, { timeoutMs: this.orderTimeoutMs })
  }

  renewChatOrder(id: string, body: PosRenewBody): Promise<PosChatOrderState> {
    return this.req('POST', `/chat-orders/${encodeURIComponent(id)}/renew`, { body, timeoutMs: this.orderTimeoutMs })
  }

  refreshChatOrder(id: string): Promise<PosChatOrderState> {
    return this.req('POST', `/chat-orders/${encodeURIComponent(id)}/refresh`, { timeoutMs: 20000 })
  }
}
